import { Component, ElementRef, HostBinding, ViewChild, OnInit, OnDestroy, Optional, Inject } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subscription, filter } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AskSimpoEventsService } from 'src/app/services/ask-simpo/ask-simpo-events.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { EventEmmiterService } from 'src/app/services/event-emmiter.service';
import { crmBearerCredential } from 'src/app/services/ask-simpo/crm-ai-auth';
import { AskSimpoHostNotificationsService } from 'src/app/services/ask-simpo/ask-simpo-host-notifications.service';

@Component({
  selector: 'app-ask-simpo-wrapper',
  template: `
    <div class="wrapper-container h-100">
      <div *ngIf="!isAskSimpoLoaded" class="loader-overlay">
        <img src="assets/animations/animation.gif" alt="logo" width="10%"/>
      </div>

      <iframe
        #askSimpoIframe
        (load)="onIframeLoad()"
        [src]="askSimpoUrl"
        width="100%"
        height="100%"
        title="Ask Simpo"
        [class.fullscreen]="isFullscreen"
        loading="lazy"
        allow="geolocation; microphone; camera; payment *; clipboard-write; clipboard-read; fullscreen;"
        [style.visibility]="isAskSimpoLoaded ? 'visible' : 'hidden'"
      ></iframe>
    </div>
  `,
  styles: [`
    :host(.pinned) {
      display: block;
      flex: 1 1 auto;
      min-height: 0;
      height: 100%;
    }
    .loader-overlay {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      background-color: #ffffff;
      z-index: 100000;
      text-align: center;
    }
    .spinner {
      border: 8px solid #f3f3f3;
      border-top: 8px solid #3498db;
      border-radius: 50%;
      width: 60px;
      height: 60px;
      animation: spin 1.5s linear infinite;
    }
    .loader-overlay p {
      margin-top: 15px;
      font-family: sans-serif;
      color: #555;
    }
    .error-text {
      color: #d9534f;
      font-weight: bold;
    }
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    iframe {
      border: none;
      height: 100% !important;
      width: 100% !important;
    }
  `]
})
export class AskSimpoWrapperComponent implements OnInit, OnDestroy {

  @ViewChild('askSimpoIframe') iframe!: ElementRef<HTMLIFrameElement>;
  isAskSimpoLoaded = false;
  sendData: any = null;
  toOpen: string = 'post';
  actionType: string = 'new-post';
  mode: string = 'post';
  private messageListener: any;
  private routeSub?: Subscription;
  private aiWriterSub?: Subscription;
  private loadedIframePath = '';
  private pinnedPath = '';

  @HostBinding('class.pinned') get isPinned(): boolean {
    return !!this.pinnedPath;
  }
  private hostContextSent = false;
  private openFromRetryTimer?: ReturnType<typeof setTimeout>;

  constructor(
    @Optional() public dialogRef: MatDialogRef<AskSimpoWrapperComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public dialogData: any,
    private router: Router,
    @Optional() private activatedRoute: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private location: Location,
    private askSimpoEvents: AskSimpoEventsService,
    private eventEmitter: EventEmmiterService,
    private notifications: AskSimpoHostNotificationsService
  ) { }

  askSimpoUrl!: SafeResourceUrl;
  isFullscreen = true;

  ngOnInit(): void {
    if (!this.dialogData) {
      this.sendData = null;
      this.actionType = 'new-post';
      this.toOpen = 'post';
      this.mode = 'post';
    }

    // Embedded under another host route (e.g. Business Settings → Models & API Keys):
    // the route's `data.askSimpoPath` pins which Ask Simpo page the iframe shows.
    this.pinnedPath = this.activatedRoute?.snapshot?.data?.['askSimpoPath'] || '';

    this.loadIframeForPath(this.pinnedPath || this.location.path());

    this.aiWriterSub = this.eventEmitter.aiWriterCalled.subscribe((res: any) => {
      if (!res) return;
      const data = res.data || res;
      this.toOpen = data.toOpen || 'post';
      this.mode = data.mode || (data.toOpen === 'ideas' || data.type === 'ideas' ? 'ideas' : data.toOpen === 'blog' || data.type === 'blog' ? 'blog' : 'post');
      this.actionType = data.actionType || data.type || 'new-post';
      this.sendData = data.sendData || null;
    });

    this.routeSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.pinnedPath) return;
        const path = this.location.path();
        if (path.startsWith('/admin/ask-simpo')) {
          this.loadIframeForPath(path);
        }
      });

    this.messageListener = (event: MessageEvent) => {
      if (event.source !== this.getIframeContentWindow() || event.origin !== this.getIframeTargetOrigin()) return;
      const type = event.data?.type;
      if (!type) return;

      if (
        type === 'ask_simpo_ready' ||
        type === 'asksimpo_ready' ||
        type === 'navigator_ready'
      ) {
        this.isAskSimpoLoaded = true;
        this.clearOpenFromRetry();
        this.hostContextSent = false;
        this.sendHostContextToIframe();
        this.sendOpenFromToIframe();
        return;
      }

      if (type === 'post_created' || type === 'idea_created' || type === 'blog_created') {
        this.handleContentSaved(type);
        return;
      }

      if (type === 'ASK_SIMPO_FULLSCREEN' || type === 'PASSBOOK_FULLSCREEN') {
        this.isFullscreen = true;
        return;
      }

      if (type === 'ASK_SIMPO_EXIT_FULLSCREEN' || type === 'PASSBOOK_EXIT_FULLSCREEN') {
        if (this.pinnedPath) return;
        this.closeAskSimpo();
        return;
      }

      const t = typeof type === 'string' ? type.toLowerCase() : '';
      if (t.startsWith('asksimpo_') || t.startsWith('ask_simpo_')) {
        if (t.includes('chat')) {
          this.askSimpoEvents.emit('chat', type, event.data);
        } else if (t.includes('project')) {
          this.askSimpoEvents.emit('project', type, event.data);
        } else if (t.includes('crm') || t.includes('lead')) {
          this.askSimpoEvents.emit('crm_leads', type, event.data);
        }
      }
    };
    window.addEventListener('message', this.messageListener);
  }

  ngOnDestroy(): void {
    const iframeContentWindow = this.getIframeContentWindow();
    if (iframeContentWindow) {
      try {
        iframeContentWindow.postMessage({
          type: 'open_from',
          data: null,
          mode: 'post',
          typeVal: 'post',
          actionType: 'new-post',
          sendData: null
        }, '*');
      } catch {}
    }
    this.sendData = null;
    this.actionType = 'new-post';
    this.clearOpenFromRetry();
    this.notifications.detach();
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }
    this.routeSub?.unsubscribe();
    this.aiWriterSub?.unsubscribe();
  }

  private loadIframeForPath(path: string): void {
    const targetPath = path.startsWith('/admin/ask-simpo') ? path : '/admin/ask-simpo';    if (targetPath === this.loadedIframePath) {
      return;
    }
    this.loadedIframePath = targetPath;
    this.hostContextSent = false;

    const rawUrl = environment.askSimpoUrl || 'http://localhost:3000';
    this.isAskSimpoLoaded = false;
    this.askSimpoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(rawUrl + targetPath);
  }

  closeAskSimpo(): void {
    const iframeContentWindow = this.getIframeContentWindow();
    if (iframeContentWindow) {
      try {
        iframeContentWindow.postMessage({
          type: 'open_from',
          data: null,
          mode: 'post',
          typeVal: 'post',
          actionType: 'new-post',
          sendData: null
        }, '*');
      } catch {}
    }
    this.sendData = null;
    this.actionType = 'new-post';
    if (this.dialogRef) {
      this.dialogRef.close();
      return;
    }
    this.router.navigate(['/admin/home']);
  }

  private getIframeContentWindow(): Window | null {
    return this.iframe?.nativeElement?.contentWindow || null;
  }

  private getIframeTargetOrigin(): string {
    const rawUrl = environment.askSimpoUrl || 'http://localhost:3000';
    try {
      return new URL(rawUrl).origin;
    } catch {
      return rawUrl;
    }
  }

  private handleContentSaved(type: string): void {
    if (!this.dialogRef) return;
    this.dialogRef.close({ saved: true, type });
    this.eventEmitter.aiWriterClosed.next({ data: { callApi: true } });
  }

  private clearOpenFromRetry(): void {
    if (this.openFromRetryTimer) {
      clearTimeout(this.openFromRetryTimer);
      this.openFromRetryTimer = undefined;
    }
  }

  private scheduleOpenFromRetry(attempt = 0): void {
    this.clearOpenFromRetry();
    if (attempt > 12) return;
    this.openFromRetryTimer = setTimeout(() => {
      this.sendOpenFromToIframe();
      this.scheduleOpenFromRetry(attempt + 1);
    }, 300);
  }

  private sendHostContextToIframe(): void {
    const iframeContentWindow = this.getIframeContentWindow();
    if (!iframeContentWindow || this.hostContextSent) return;

    const targetOrigin = this.getIframeTargetOrigin();
    this.notifications.attach(
      iframeContentWindow,
      targetOrigin,
      (link: string) => {
        const route = link.startsWith('/') ? link : `/${link}`;
        this.router.navigateByUrl(`/admin/ask-simpo${route}`);
      }
    );

    // Send the beeOs login credential only to this configured iframe origin.
    // The backend validates the token; host IDs never grant permissions.
    const credential = crmBearerCredential();
    iframeContentWindow.postMessage({
      type: 'asksimpo:host-context',
      payload: {
        businessId: localStorage.getItem('businessId'),
        staffId: localStorage.getItem('staffId'),
        token: credential ? credential.replace(/^Bearer\s+/i, '') : null
      }
    }, targetOrigin);

    const bDetails = localStorage.getItem('bDetails');
    const staffType = localStorage.getItem('staffType');
    const staffId = localStorage.getItem('staffId');
    const staffName = localStorage.getItem('staffName');
    const staffEmail = localStorage.getItem('staffEmail');
    const businessId = localStorage.getItem('businessId');
    const businessName = localStorage.getItem('businessName');
    const business = localStorage.getItem('business');
    const mobileLogin = localStorage.getItem('mobileLogin');
    const permission = localStorage.getItem('permission');
    const path = this.router.url;

    iframeContentWindow.postMessage({ type: 'set_path', path }, '*');
    iframeContentWindow.postMessage({ type: 'localStorage', data: JSON.stringify(bDetails) }, '*');
    iframeContentWindow.postMessage({ type: 'bDetails', data: bDetails }, '*');
    iframeContentWindow.postMessage({ type: 'staffType', data: JSON.stringify(staffType) }, '*');
    iframeContentWindow.postMessage({ type: 'staffId', data: JSON.stringify(staffId) }, '*');
    iframeContentWindow.postMessage({ type: 'staffName', data: JSON.stringify(staffName) }, '*');
    iframeContentWindow.postMessage({ type: 'staffEmail', data: JSON.stringify(staffEmail) }, '*');
    iframeContentWindow.postMessage({ type: 'businessId', data: JSON.stringify(businessId) }, '*');
    iframeContentWindow.postMessage({ type: 'businesName', data: JSON.stringify(businessName) }, '*');
    iframeContentWindow.postMessage({ type: 'business', data: JSON.stringify(business) }, '*');
    iframeContentWindow.postMessage({ type: 'permission', data: JSON.stringify(permission) }, '*');
    iframeContentWindow.postMessage({ type: 'mobileLogin', data: JSON.stringify(mobileLogin) }, '*');
    this.hostContextSent = true;
  }

  private sanitizeSendData(sendData: any): any {
    if (sendData == null) return null;
    try {
      return JSON.parse(JSON.stringify(sendData));
    } catch {
      return sendData;
    }
  }

  private sendOpenFromToIframe(): void {
    const iframeContentWindow = this.getIframeContentWindow();
    if (!iframeContentWindow) return;

    const isDialogMode = Boolean(this.dialogData);
    if (isDialogMode) {
      const mode = this.dialogData?.mode || (this.dialogData?.type === 'ideas' || this.dialogData?.toOpen === 'ideas' || this.mode === 'ideas' || this.toOpen === 'ideas' ? 'ideas' : this.dialogData?.type === 'blog' || this.dialogData?.toOpen === 'blog' || this.mode === 'blog' || this.toOpen === 'blog' ? 'blog' : 'post');
      const actionType = this.dialogData?.actionType || this.dialogData?.type || this.actionType || 'new-post';
      
      const isNewAction = actionType === 'new-post' || actionType === 'new-idea' || actionType === 'new-blog' || actionType?.startsWith('new') || actionType?.startsWith('create');
      const rawSendData = isNewAction
        ? (this.dialogData?.sendData || null)
        : (this.dialogData?.sendData || this.sendData || null);

      const sendData = this.sanitizeSendData(rawSendData);
      const openFrom = this.dialogData?.openFrom || (mode === 'blog' ? 'blog' : mode === 'ideas' ? 'ideas' : 'social_flow');

      iframeContentWindow.postMessage({
        type: 'open_from',
        data: openFrom,
        mode: mode,
        typeVal: mode,
        actionType: actionType,
        sendData: sendData
      }, '*');
      return;
    }

    iframeContentWindow.postMessage({
      type: 'open_from',
      data: null,
      mode: 'post',
      typeVal: 'post',
      actionType: 'new-post',
      sendData: null
    }, '*');
  }

  onIframeLoad(): void {
    setTimeout(() => {
      this.isAskSimpoLoaded = true;
    }, 1200);

    this.sendHostContextToIframe();
    this.sendOpenFromToIframe();
    this.scheduleOpenFromRetry();
  }
}
